import { useEffect } from 'react';
import type { FC } from 'react';
import { Flex } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUpdateProductItem } from '../../../../bff-api/baskets/useUpdateProductItem';
import { BffQueryKey } from '../../../../bff-api/model/BffQueryKey';
import { Field } from '../../../../components/field';
import { useEditAttendeesCartFields } from '../../../../components/forms/useEditAttendeesCartFields';
import { useIsMobile } from '../../../../hooks/use-is-mobile';
import { queryClient } from '../../../../slt/UseQueryProvider';
import { Text } from '../../../../ui-components';
import { Drawer } from '../../../../ui-components/drawer';
import { formatPhoneNumber } from '../../../../utils/phone-utils';
import { useCartState } from '../../contexts/useCartState';
import { useCulinaryDrawer } from '../../contexts/useCulinaryDrawer';
import { getProductItemAttendees } from '../../utils/cart-utils';
import { InStoreClassPolicy } from './InStoreClassPolicy';
import { OnlineClassPolicy } from './OnlineClassPolicy';
import type { Attendee } from '../../models/Attendee';

export const EditCulinaryDrawer: FC = () => {
    const intl = useIntl();
    const updateProductItem = useUpdateProductItem();
    const { culinaryProducts, itemRefs, headerHeight } = useCartState();
    const {
        isEditCulinaryDrawerOpen,
        culinaryDrawerMode,
        editCulinaryItemId,
        editCulinaryAttendee,
        editCulinaryIndex,
        onCloseEditCulinaryDrawer,
        onAddAttendee,
    } = useCulinaryDrawer();
    const isMobile = useIsMobile();

    const form = useForm<Attendee>({
        defaultValues: {
            fname: '',
            lname: '',
            phone: '',
            email: '',
            checkTermsField: false,
        },
        reValidateMode: 'onChange',
        mode: 'onChange',
    });

    useEffect(() => {
        if (culinaryDrawerMode === 'edit' && editCulinaryAttendee) {
            form.setValue('fname', editCulinaryAttendee?.fname);
            form.setValue('lname', editCulinaryAttendee?.lname);
            form.setValue('email', editCulinaryAttendee?.email);
            form.setValue('phone', formatPhoneNumber(editCulinaryAttendee?.phone));
        }
    }, [editCulinaryAttendee, culinaryDrawerMode, form]);

    const fields = useEditAttendeesCartFields(form);

    const culinaryItem = culinaryProducts.find(
        (culinaryProduct) => culinaryProduct.itemId === editCulinaryItemId,
    );

    if (!culinaryItem) {
        return null;
    }

    const closeDrawer = (): void => {
        form.reset();
        const currentItemRef = itemRefs.current?.get(culinaryItem.productId);
        if (isMobile) {
            window.scrollTo({
                top:
                    (currentItemRef?.getBoundingClientRect().top ?? 0) +
                    window.scrollY -
                    headerHeight,
                behavior: 'smooth',
            });
        }
        onCloseEditCulinaryDrawer();
    };

    const submitAttendee = async (): Promise<void> => {
        await form.trigger();
        const errorsLength = Object.keys(form.formState.errors).length;
        if (errorsLength) {
            return;
        }

        const classAttendees = getProductItemAttendees(culinaryItem);

        if (culinaryDrawerMode === 'add') {
            classAttendees.attendees.push({
                fname: form.getValues('fname'),
                lname: form.getValues('lname'),
                email: form.getValues('email'),
                phone: form.getValues('phone'),
            } as unknown as Attendee);
        } else if (editCulinaryAttendee) {
            editCulinaryAttendee.fname = form.getValues('fname');
            editCulinaryAttendee.lname = form.getValues('lname');
            editCulinaryAttendee.email = form.getValues('email');
            editCulinaryAttendee.phone = form.getValues('phone');
            const updatedAttendee = {
                productId: culinaryItem.productId,
                itemId: culinaryItem.itemId,
                c_attendeeDetails: JSON.stringify({
                    attendees: (culinaryItem?.c_culinaryClass?.attendees ?? []).map(
                        ({ id, ...rest }) => rest,
                    ),
                }),
                quantity: classAttendees.attendees.length,
            };

            updateProductItem.mutate(
                { ...updatedAttendee },
                {
                    onSuccess: (payload) => {
                        queryClient.setQueryData([BffQueryKey.BASKET], payload);
                        closeDrawer();
                    },
                },
            );

            return;
        }

        onAddAttendee(culinaryItem, classAttendees);
        closeDrawer();
    };

    const attendeesNumber = culinaryItem?.c_culinaryClass?.attendees.length ?? 0;

    return (
        <Drawer
            id="edit-culinary-item-drawer"
            title={
                culinaryDrawerMode === 'add'
                    ? intl.formatMessage({
                          id: 'cart.edit-culinary.add-attendee.drawer',
                          defaultMessage: 'Add attendee',
                      })
                    : intl.formatMessage({
                          id: 'cart.edit-culinary.edit-attendee.drawer',
                          defaultMessage: 'Edit attendee',
                      })
            }
            isOpen={isEditCulinaryDrawerOpen}
            onClose={closeDrawer}
            isFullPage
            footerElement={
                <Flex>
                    <Field {...fields.checkTermsField} />
                </Flex>
            }
            callToAction={{
                children:
                    culinaryDrawerMode === 'add'
                        ? intl.formatMessage({
                              id: 'cart.edit-culinary.add-attendee.cta',
                              defaultMessage: 'Add Attendee',
                          })
                        : intl.formatMessage({
                              id: 'cart.edit-culinary.edit-attendee.cta',
                              defaultMessage: 'Edit Attendee',
                          }),

                onClick: submitAttendee,
            }}
        >
            <Flex gap={6} direction="column">
                <Text id="attendee-disclaimer">
                    <FormattedMessage
                        id="cart.edit-culinary.attendee-disclaimer"
                        defaultMessage="Attendee information will be used to send class information and recipe packet."
                    />
                </Text>
                <Flex gap={4} direction="column">
                    <Text style="bold">
                        <FormattedMessage
                            id="cart.edit-culinary.new-attendee"
                            defaultMessage="Attendee #{attendeeNumber}"
                            values={{
                                attendeeNumber:
                                    culinaryDrawerMode === 'add'
                                        ? attendeesNumber + 1
                                        : editCulinaryIndex,
                            }}
                        />
                    </Text>
                    <Field {...fields.firstName} />
                    <Field {...fields.lastName} />
                    <Field {...fields.email} />
                    <Field {...fields.phone} />
                </Flex>
                <Flex>
                    {culinaryItem.c_culinaryClass.isOnline ? (
                        <OnlineClassPolicy id={culinaryItem.productId} />
                    ) : (
                        <InStoreClassPolicy id={culinaryItem.productId} />
                    )}
                </Flex>
            </Flex>
        </Drawer>
    );
};
